import styles from "./menu.module.css";
import {
  Breadcrumb,
  Divider,
  Button,
  Card,
  Form,
  Input,
  Row,
  Col,
  Select,
  Switch,
  Tree,
  Popconfirm,
  Empty,
  Spin,
} from "antd";
import React, { useState, useEffect, dispatch } from "react";
import * as Icon from "react-feather";
import templateContentService from "../../services/template-content-service";
import { useDispatch } from "react-redux";
import appLoaderAction from "../../redux/action/app-loader-action";
import notifyUtitls from "../../utils/notify-utils";
import menuService from "../../services/menu-service";
import SecurityUtils from "../../utils/security-utils";
import constantService from "../../services/constant-service";
import menuPreviewAction from "../../redux/action/menu-action";
import debounce from "lodash/debounce";
import { PREVIEW_TYPE, VISIBILITY_DEFAULT, VISIBILITY_OPTIONS } from "../../constants/app-constants";
import { openWebsitePreview } from "../../utils/preview-utils";
import { clearedTargetsFor } from "../../utils/menu-target-utils";

const { Option } = Select;

function onChange(checked) {
  console.log(`switch to ${checked}`);
}

const DebounceSelect = ({ fetchOptions, debounceTimeout = 800, ...props }) => {
  const [fetching, setFetching] = React.useState(false);
  const [options, setOptions] = useState([]);
  const fetchRef = React.useRef(0);

  useEffect(() => {
    debounceFetcher();
  }, []);

  const debounceFetcher = React.useMemo(() => {
    const loadOptions = (value) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setOptions([]);
      setFetching(true);
      fetchOptions(value).then((newOptions) => {
        if (fetchId !== fetchRef.current) {
          // for fetch callback order
          return;
        }

        const newResult = newOptions.map((item) => {
          return {
            value: item.templateId,
            label: item.mainTitle.titleTH,
            key: item.templateId,
          };
        });
        setOptions(newResult);
        setFetching(false);
      });
    };

    return debounce(loadOptions, debounceTimeout);
  }, [fetchOptions, debounceTimeout]);

  return (
    // labelInValue
    <Select
      filterOption={false}
      onSearch={debounceFetcher}
      notFoundContent={fetching ? <Spin size="small" /> : null}
      {...props}
      options={options}
    />
  );
};

const MenuManagement = () => {
  const dispatch = useDispatch();
  const [menus, setMenus] = useState(null);
  const [contentList, setContentList] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [selected, setSelected] = useState(null);
  const [condition, setCondition] = useState(null);
  const [currentGroupMenu, setCurrentGroupMenu] = useState(null);
  const [menuType, setMenuType] = useState([]);
  // NMU-559: the news categories a NEWS menu item can point at.
  const [newsTypeList, setNewsTypeList] = useState([]);
  // NMU-571: menuId + updateTime of the menu this page loaded.
  const [menuVersion, setMenuVersion] = useState(null);
  const [template, setTemplate] = useState(null);
  const [templateType, setTemplateType] = useState(null);
  const [form] = Form.useForm();
  const [currentMenuType, setCurrentMenuType] = useState(null);
  const [isDelete, setIsDelete] = useState(false);

  //typeahead seach
  const [pagination, setPagination] = useState({
    currentPage: 1,
    rowPerPage: 999,
    rowCount: 0,
  });

  useEffect(() => {
    async function initData() {
      const { menuType, newsType } = await constantService.getMenuAndNewsType();
      setMenuType(menuType);
      setNewsTypeList(newsType || []);
    }

    initData();
    doGetContentList(null);
    setCondition("COMMON");
  }, []);

  useEffect(() => {
    if (condition) {
      doInit();
    }
  }, [condition]);

  useEffect(() => {
    if (isDelete && menus) {
      onSaveMenu();
    } else {
      setIsDelete(false);
    }
  }, [menus, isDelete]);

  const doInit = async () => {
    try {
      dispatch(appLoaderAction.enable());

      await doLoadMenu();

      dispatch(appLoaderAction.disale());
    } catch (err) {
      dispatch(appLoaderAction.disale());
      dispatch(notifyUtitls.error(err));
    }
  };

  const doGetContentList = async (keyword) => {
    try {
      const newPaginate = {
        ...pagination,
        currentPage: 1,
      };

      const newCondition = { title: keyword };
      const result = await templateContentService.search(
        newCondition,
        newPaginate
      );
      setContentList(result.list);
      setPagination(result.pagination ? result.pagination : newPaginate);

      dispatch(appLoaderAction.disale());
    } catch (err) {
      dispatch(appLoaderAction.disale());
      dispatch(notifyUtitls.error(err));
    }
  };

  const doLoadMenu = async () => {
    const newPaginate = { ...pagination, currentPage: 1 };
    const newCondition = { groupMenu: condition };

    const result = await menuService.search(newCondition, newPaginate);
    if (result.menu) {
      setCurrentGroupMenu(result.menu.groupMenu);
      /**
       * NMU-571: remember which version of the menu this page is editing.
       *
       * The save used to send only { groupMenu, menu }, so the service saw no
       * updateTime, skipped its concurrency check entirely and replaced the
       * document wholesale — two editors saving in the same window silently
       * overwrote one another, with both told "บันทึกสำเร็จ". Sending the
       * version back is what lets the service notice, and what makes the
       * warning added for this ticket reachable at all.
       */
      setMenuVersion({
        menuId: result.menu.menuId,
        updateTime: result.menu.updateTime,
      });
      const newMenus = await result.menu.menu.map((item) => {
        return getMenuKey(item);
      });

      setMenus(newMenus);
    } else {
      setCurrentGroupMenu(null);
      setMenuVersion(null);
      setMenus([]);
    }
  };

  const getMenuKey = (item) => {
    const { children } = item;
    if (children && children.length > 0) {
      const subMenus = children.map((item) => {
        return getMenuKey(item);
      });
      return {
        key: item.menuId,
        ...item,
        children: [...subMenus],
        status: item.status === "true",
        title: item.menuTitle?.titleTH
          ? item.menuTitle?.titleTH
          : item.menuName,
        menuTitle: {
          titleTH: item.menuTitle?.titleTH
            ? item.menuTitle?.titleTH
            : item.menuName,
          titleEN: item.menuTitle?.titleEN,
        },
      };
    } else {
      return {
        key: item.menuId,
        ...item,
        children: item.menu,
        status: item.status === "true",
        title: item.menuTitle?.titleTH
          ? item.menuTitle?.titleTH
          : item.menuName,
        menuTitle: {
          titleTH: item.menuTitle?.titleTH
            ? item.menuTitle?.titleTH
            : item.menuName,
          titleEN: item.menuTitle?.titleEN,
        },
      };
    }
  };

  /**
   * NMU-560: only the name is still required.
   *
   * ประเภท, URL and เนื้อหา are optional now — an item left without them is a
   * heading, and the website renders it as plain text instead of a link. What
   * remains is that every item, leaf or section, needs a name to render at all.
   */
  const loopMenuValidate = (data, name, callback) => {
    for (let i = 0; i < data.length; i++) {
      const datai = data[i];

      if (!datai[name]) {
        return callback(data[i], i, data);
      }

      if (datai.children) {
        loopMenuValidate(datai.children, name, callback);
      }
    }
  };

  const validateMenu = () => {
    let isMenuNameInvalid;

    loopMenuValidate(menus, "menuName", (item, index, arr) => {
      isMenuNameInvalid = " ชื่อเมนู";
    });

    if (isMenuNameInvalid) {
      notifyUtitls.warning(
        "แจ้งเตือน",
        "โปรดระบุ" + isMenuNameInvalid + "ให้ครบถ้วนทุกรายการ"
      );
      return false;
    }

    return true;
  };

  const onSaveMenu = async () => {
    try {
      if (isDelete) {
        setIsDelete(false);
      }
      const values = await form.validateFields();
      if (validateMenu()) {
        try {
          dispatch(appLoaderAction.enable());

          const bean = getSaveBean();

          const result = await menuService.edit({ bean: bean });

          doLoadMenu();

          dispatch(appLoaderAction.disale());

          /**
           * NMU-571: do not call a partial save a success.
           *
           * When the stored menu has moved on since this page loaded it, the
           * service merges instead of replacing: edits and additions land, but
           * anything the editor DELETED stays in the menu. It has always said so
           * (`versionChanged`, and an English `message` before that) and this
           * page has always thrown the answer away and toasted "บันทึกสำเร็จ" —
           * so a deletion could disappear while the UI reported success.
           *
           * The tree is reloaded either way by doLoadMenu() above, so the editor
           * is now told what happened and is already looking at the real state.
           */
          if (result?.versionChanged || result?.message) {
            notifyUtitls.warning(
              "บันทึกได้บางส่วน",
              "เมนูถูกแก้ไขจากที่อื่นระหว่างที่คุณกำลังแก้ไข ระบบบันทึกการเพิ่มและการแก้ไขให้แล้ว แต่รายการที่คุณลบยังไม่ถูกลบ กรุณาตรวจสอบรายการเมนูที่โหลดใหม่แล้วลบอีกครั้ง"
            );
          } else {
            notifyUtitls.success("สำเร็จ", "บันทึกสำเร็จ");
          }
        } catch (err) {
          notifyUtitls.error(err);
          dispatch(appLoaderAction.disale());
        }
      }
    } catch (errorInfo) {
      notifyUtitls.warning("แจ้งเตือน", "โปรดระบุข้อมูลให้ครบถ้วน");
    }
  };

  /**
   * NMU-417: a node with children is a section header, and the form shows it no
   * ประเภท/URL/เนื้อหา fields — but "+ เพิ่มเมนู" seeds every new node with
   * menuType "CONTENT" and a null templateId, and that seed stays on the node
   * after sub-items are added under it. Sending it made the backend validate
   * the section as a content leaf and reject the whole save with
   * "กรุณาระบุข้อมูล". Send what the form actually shows for each node.
   */
  const toSaveNode = (item) => {
    const children = item.children ? item.children.map(toSaveNode) : [];
    const node = {
      ...item,
      menuName: item.title,
      children,
    };

    if (children.length > 0) {
      node.menuType = null;
      node.url = null;
      node.templateId = null;
      node.newsType = null;
    } else {
      // NMU-560: "โปรดระบุ" is the empty option, and clearing a target leaves an
      // empty string behind. Send null so the item is stored as unlinked rather
      // than as a type the backend has to interpret.
      node.menuType = node.menuType || null;
      node.url = node.url || null;
      node.templateId = node.templateId || null;
      node.newsType = node.newsType || null;
    }

    return node;
  };

  const getSaveBean = () => {
    const newMenu = menus.map(toSaveNode);

    // NMU-571: carry the version this page loaded, so the service can tell an
    // ordinary save from one racing another editor. Same version means the same
    // wholesale replace as before; a changed one comes back flagged.
    const bean = {
      ...(menuVersion || {}),
      groupMenu: condition,
      menu: newMenu,
    };

    return bean;
  };

  const onCancelMenu = async () => {
    try {
      dispatch(appLoaderAction.enable());

      await doLoadMenu();

      dispatch(appLoaderAction.disale());
    } catch (err) {
      dispatch(appLoaderAction.disale());
      dispatch(notifyUtitls.error(err));
    }
  };

  const onAddMenu = () => {
    const newMenu = [...menus];

    newMenu.push({
      key: SecurityUtils.uuidv4(),
      title: "เมนู" + newMenu.length,
      menuName: "เมนู" + newMenu.length,
      menuType: "CONTENT",
      url: null,
      templateId: null,
      newsType: null,
      hasChild: false,
      status: true,
      visibility: VISIBILITY_DEFAULT,
      children: [],
      menuTitle: { titleTH: null, titleEN: null },
    });

    setMenus(newMenu);
  };

  const onRemoveMenu = () => {
    const newMenu = menus.map((item) => {
      return removeMenu(item, selected);
    });

    const removedMenus = newMenu.filter((item) => item !== null);
    setMenus(removedMenus);
    setIsDelete(true);

    setTemplateType(null);
    setSelected(null);
  };

  const removeMenu = (menu, selected) => {
    if (menu.key === selected.key) {
      return null;
    } else {
      if (menu.children && menu.children.length > 0) {
        const children = menu.children.map((item) => {
          return removeMenu(item, selected);
        });

        return {
          ...menu,
          children: [...children.filter((item) => item !== null)],
        };
      } else {
        return menu;
      }
    }
  };

  const replaceMenu = (menu, selected) => {
    if (menu.menuId === selected.menuId) {
      return {
        ...selected,
      };
    } else {
      if (menu.children && menu.children.length > 0) {
        const children = menu.children.map((item) => {
          return replaceMenu(item, selected);
        });
        return {
          ...menu,
          children: [...children],
        };
      } else {
        return menu;
      }
    }
  };

  const onPreview = async () => {
    try {
      dispatch(appLoaderAction.enable());

      const bean = getSaveBean();
      // dispatch(menuPreviewAction.addMenu({bean: bean}));

      // console.log('{bean: bean} ', {bean: bean});

      await openWebsitePreview({ type: PREVIEW_TYPE.menu, listBean: [bean] });
      dispatch(appLoaderAction.disale());
    } catch (err) {
      dispatch(appLoaderAction.disale());
      notifyUtitls.error(err);
    }
  };

  const onDragEnter = (info) => {
    // console.error(info);
  };

  const loop = (data, key, callback) => {
    for (let i = 0; i < data.length; i++) {
      if (data[i].key === key) {
        return callback(data[i], i, data);
      }
      if (data[i].children) {
        loop(data[i].children, key, callback);
      }
    }
  };

  const onDrop = (info) => {
    const dropKey = info.node.props.eventKey;
    const dragKey = info.dragNode.props.eventKey;
    const dropPos = info.node.props.pos.split("-");
    const dropPosition =
      info.dropPosition - Number(dropPos[dropPos.length - 1]);
    let ableUpdate = false;

    const str = JSON.stringify(menus);
    const data = [...JSON.parse(str)];

    //Start Validate
    loop(data, dropKey, (item) => {
      // const parent = item;
      // if (!parent.fixed) {
      ableUpdate = true;
      // }
    });

    if (!ableUpdate) {
      return null;
    }

    // Find dragObject
    let dragObj;
    loop(data, dragKey, (item, index, arr) => {
      arr.splice(index, 1);
      dragObj = item;
    });

    // loopMenu(data, dragKey, (item, index, arr) => {
    // 	arr.splice(index, 1);
    // 	dragObj = item;
    // });

    let isUpdateHasChild = true;

    if (!info.dropToGap) {
      // Drop on the content

      loop(data, dropKey, (item) => {
        if (!item.children) {
          isUpdateHasChild = false;
        }

        item.children = item.children || [];
        // where to insert
        item.children.push(dragObj);
      });

      // loopMenu(data, dropKey, (item) => {
      // 	// if (!item.menu) {
      // 	// 	isUpdateHasChild = false;
      // 	// }

      // 	item.menu = item.menu || [];
      // 	// where to insert
      //   item.menu.push(dragObj);
      // });
    } else if (
      (info.node.props.children || []).length > 0 && // Has children
      info.node.props.expanded && // Is expanded
      dropPosition === 1 // On the bottom gap
    ) {
      loop(data, dropKey, (item) => {
        item.children = item.children || [];
        item.children.unshift(dragObj);
      });

      // loopMenu(data, dropKey, (item) => {
      // 	item.menu = item.menu || [];
      // 	item.menu.unshift(dragObj);
      // });
    } else {
      let ar;
      let i;

      // let arm;
      // let im;
      loop(data, dropKey, (item, index, arr) => {
        ar = arr;
        i = index;
      });

      // loopMenu(data, dropKey, (item, index, arr) => {
      // 	arm = arr;
      // 	im = index;
      // });

      if (dropPosition === -1) {
        ar.splice(i, 0, dragObj);
      } else {
        ar.splice(i + 1, 0, dragObj);
      }

      // if (dropPosition === -1) {
      // 	arm.splice(im, 0, dragObj);
      // } else {
      // 	arm.splice(im + 1, 0, dragObj);
      // }
    }

    // if (!isUpdateHasChild) {
    // 	return null;
    // }

    setMenus(data);
  };

  const onSelectMenu = async (selectedKeys, { selectedNodes }) => {
    if (selectedNodes && selectedKeys) {
      const selected = selectedNodes[0];
      if (selected) {
        if (selected.templateId) {
          await getTemplateById(selected.templateId);
        } else {
          setTemplate({ mainTitle: { titleTH: null } });
        }
        setSelectedKeys(selectedKeys);
      } else {
        setSelectedKeys([]);
      }

      setCurrentMenuType(selected?.menuType);
      setSelected(selected);

      onSelectedMenuToForm(selected);
    }
  };

  const getTemplateById = async (id) => {
    try {
      dispatch(appLoaderAction.enable());
      const obj = await templateContentService.getById({ id: id });
      setTemplate(obj.bean);
      dispatch(appLoaderAction.disale());
    } catch (err) {
      dispatch(appLoaderAction.disale());
      notifyUtitls.error(err);
    }
  };

  const onSelectedMenuToForm = (selected) => {
    if (form) {
      form.resetFields();
    }

    if (selected) {
      form.setFieldsValue({
        title: selected.title,
        titleTH: selected?.menuTitle?.titleTH,
        titleEN: selected?.menuTitle?.titleEN,
        menuType: selected.menuType,
        url: selected.url,
        templateId: selected.templateId,
        // NMU-559: the chosen news category, when the item is a NEWS item.
        newsType: selected.newsType,
        // NMU-609: off unless the record says otherwise, including for every
        // item saved before the toggle existed.
        enableNewTab: !!selected.enableNewTab,
        status: selected.status === "true" || selected.status,
        visibility: selected.visibility ? selected.visibility : VISIBILITY_DEFAULT,
      });
    } else {
      setCurrentMenuType(null);
    }
  };

  const onHandleValue = (name) => {
    return (e) => {
      const newMenus = [...menus];
      loop(newMenus, selected.key, (item) => {
        setValue(e, item, name);
        item.menuName = item.title;
        item.menuUrl = item.urlSuffix;
      });

      setMenus(newMenus);
    };
  };

  const setValue = (e, item, name) => {
    if (e.target) {
      if (e.target.value !== undefined) {
        const { value } = e.target;
        if (name === "titleTH") {
          item["menuTitle"].titleTH = value;
          item["title"] = value;
        } else if (name === "titleEN") {
          item["menuTitle"].titleEN = value;
        } else {
          item[name] = value;
        }
      } else if (e.target.checked !== undefined) {
        const { checked } = e.target;
      }
    } else {
      const value = e;
      item[name] = value;

      if (name === "menuType") {
        setCurrentMenuType(value);

        /*
          NMU-574: the destination follows the type on the record, not just on
          screen. Switching ประเภท used to swap which target field was visible
          and leave the old one on the node, so an item could be saved as ข่าว
          while still carrying the URL of a single article — which is what every
          ข่าว item on uat looks like today.

          Cleared on the node and in the form together: the node is what gets
          saved, and the form is what the editor sees if they switch back.
        */
        const cleared = clearedTargetsFor(value);
        Object.assign(item, cleared);
        form.setFieldsValue(cleared);
      }
    }
  };

  const renderTreeMenu = (groupMenu) => {
    return (
      <div>
        <Button type="primary" className="w-100" onClick={onAddMenu}>
          <Icon.Plus className="me-1" size={18} /> เพิ่มเมนู
        </Button>
        <h5 className="color-primary mt-3 mb-2">รายการเมนู </h5>
        <div className="boder-main">
          {menus && menus.length > 0 ? (
            <Tree
              key={"tree-menu" + groupMenu}
              className="draggable-tree"
              defaultExpandAll={true}
              draggable
              blockNode
              onDragEnter={onDragEnter}
              onDrop={onDrop}
              selectedKeys={selectedKeys}
              treeData={menus}
              onSelect={onSelectMenu}
            />
          ) : null}
        </div>
      </div>
    );
  };

  const onCurrentMenuTypeChange = () => {
    return (value) => {
      setCurrentMenuType(value);
    };
  };

  const onSelectAutoComplete = (value) => {
    // console.log("onSelect", value);
  };

  const handleSearch = (value) => {
    // console.log('handleSearch ', value);
    doGetContentList(value);
  };

  const fetchContent = async (keyword) => {
    try {
      const newPaginate = {
        ...pagination,
        currentPage: 1,
      };

      const newCondition = { title: keyword };
      const result = await templateContentService.search(
        newCondition,
        newPaginate
      );
      const newResult = result.list.map((item) => {
        return {
          ...item,
          value: item.templateId,
          label: item.mainTitle.titleTH,
          key: item.templateId,
        };
      });

      return newResult;
    } catch (err) {
      dispatch(notifyUtitls.error(err));
    }
  };

  const renderSelectedMenu = (groupMenu) => {
    return (
      <div key={"selected-menu" + groupMenu}>
        {selected ? (
          <Form
            layout="vertical"
            name="add_edit_menu"
            preserve={false}
            form={form}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label={"ชื่อเมนูภาษาไทย"}
                  name="titleTH"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Input onChange={onHandleValue("titleTH")} />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item label={"ชื่อเมนูภาษาอังกฤษ"} name="titleEN">
                  <Input onChange={onHandleValue("titleEN")} />
                </Form.Item>
              </Col>
              {!selected.children ||
              (selected.children && selected.children.length == 0) ? (
                <Col span={12}>
                  {/* NMU-560: optional. A menu item with no type is a heading —
                      the site renders it as plain text rather than a link. */}
                  <Form.Item label="ประเภท" name="menuType">
                    <Select
                      onChange={onHandleValue("menuType")}
                      value={currentMenuType}
                    >
                      <Option value="">โปรดระบุ</Option>
                      {renderMenuType()}
                    </Select>
                  </Form.Item>
                </Col>
              ) : null}
              {!selected.children ||
              (selected.children && selected.children.length == 0) ? (
                <Col span={24}>
                  {/* NMU-560: the target follows the type, and both are optional.
                      With no type selected there is nothing to point at, so no
                      target field is shown at all. */}
                  {!currentMenuType ? null : currentMenuType === "URL" ? (
                    <>
                      <Form.Item name="url" label="URL">
                        <Input onChange={onHandleValue("url")} />
                      </Form.Item>
                      {/* NMU-609: offered for ลิงค์(URL) only. A เนื้อหา page and
                          a ข่าว listing are routes inside this site, and opening
                          one in a new tab leaves the visitor with two copies of
                          NMU. Default off — a menu item opening in place is what
                          a visitor expects. */}
                      <Form.Item
                        label="เปิดในแท็บใหม่ (Open in New Tab)"
                        name="enableNewTab"
                        valuePropName="checked"
                      >
                        <Switch onChange={onHandleValue("enableNewTab")} />
                      </Form.Item>
                    </>
                  ) : currentMenuType === "NEWS" ? (
                    /* NMU-559: a NEWS item opens the News listing filtered to one
                       category. The list is the same news taxonomy the News page
                       filters by, fetched from the constants endpoint. */
                    <div id="menu-news-type-field">
                      <Form.Item name="newsType" label="ประเภทข่าว">
                        <Select
                          onChange={onHandleValue("newsType")}
                          placeholder="โปรดระบุ"
                          allowClear
                        >
                          {renderNewsType()}
                        </Select>
                      </Form.Item>
                    </div>
                  ) : (
                    <Form.Item name="templateId" label="เนื้อหา">
                      {/* <Select 
                        onChange={onHandleValue('templateId')} 
                        showSearch
                        filterOption={false}
                        onSearch={handleSearch}
                        placeholder="พิมพ์เพื่อค้นหา"
                        fetchOptions={fetchContent}>
                      </Select> */}
                      <DebounceSelect
                        onChange={onHandleValue("templateId")}
                        showSearch
                        filterOption={false}
                        value={{
                          value: selected.templateId,
                          label: template.mainTitle.titleTH,
                        }}
                        placeholder="พิมพ์เพื่อค้นหา"
                        fetchOptions={fetchContent}
                      />
                    </Form.Item>
                  )}

                  <Form.Item
                    label="สถานะ"
                    name="status"
                    valuePropName="checked"
                  >
                    <Switch onChange={onHandleValue("status")} />
                  </Form.Item>
                </Col>
              ) : null}

              <Col span={24}>
                <Form.Item label="การเผยแพร่ (Visibility)" name="visibility">
                  <Select onChange={onHandleValue("visibility")}>
                    {VISIBILITY_OPTIONS.map(({ value, label }) => (
                      <Option value={value} key={value}>
                        {label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col span={24}>
                <div className="d-flex justify-content-between">
                  <div>
                    <Button type="primary" onClick={onSaveMenu}>
                      <Icon.Save className="me-2" size={17} />
                      บันทึกเมนู
                    </Button>

                    <Popconfirm
                      title="ยืนยันการลบข้อมูล ?"
                      onConfirm={() => onRemoveMenu()}
                      okText="ยืนยัน"
                      cancelText="ยกเลิก"
                    >
                      <Button type="primary" danger className="mx-2">
                        <Icon.Trash size={17} className="me-2" />
                        ลบเมนู
                      </Button>
                    </Popconfirm>
                  </div>
                  {/*               <div>
                    <Button className="mx-2 btn-line" onClick={() => onPreview()}>
                      <Icon.Eye className="me-1" size={18} /> ดูตัวอย่างเว็บไซต์
                    </Button>
                  </div> */}
                </div>
              </Col>
            </Row>
          </Form>
        ) : (
          <Empty />
        )}
      </div>
    );
  };

  const renderMenuType = () => {
    return menuType.map(({ value, labelTh }) => (
      <Option value={value} key={value}>
        {labelTh}
      </Option>
    ));
  };

  /**
   * NMU-559: the news categories, grouped the way the News page groups them
   * (NMU-412 — the taxonomy has main groups and sub-types). Falls back to a flat
   * list when the API sends no grouping, so an older service still renders.
   */
  const renderNewsType = () => {
    const hasGroups = newsTypeList.some((item) => item.groupValue);

    if (!hasGroups) {
      return newsTypeList.map(({ value, labelTh }) => (
        <Option value={value} key={value}>
          {labelTh}
        </Option>
      ));
    }

    const groups = [];
    const byKey = {};
    newsTypeList.forEach((item) => {
      const key = item.groupValue;
      if (!byKey[key]) {
        byKey[key] = { label: item.groupLabelTh || key, items: [] };
        groups.push(byKey[key]);
      }
      byKey[key].items.push(item);
    });

    return groups.map((group) => (
      <Select.OptGroup label={group.label} key={group.label}>
        {group.items.map(({ value, labelTh }) => (
          <Option value={value} key={value}>
            {labelTh}
          </Option>
        ))}
      </Select.OptGroup>
    ));
  };

  const renderContentList = () => {
    return contentList.map(({ templateId, mainTitle }) => (
      <Option value={templateId} key={templateId} text={mainTitle.titleTH}>
        {mainTitle.titleTH ? mainTitle.titleTH : mainTitle.titleEN}
      </Option>
    ));
  };

  return (
    <div>
      <h2 className="color-primary-title">เมนู</h2>
      <Breadcrumb>
        <Breadcrumb.Item>การจัดการเนื้อหา</Breadcrumb.Item>
        <Breadcrumb.Item>เมนู</Breadcrumb.Item>
      </Breadcrumb>
      <Divider className="mt-2 mb-4" />
      {/* NMU-407: one Common menu — user-group tabs removed. */}
      <Row gutter={16}>
        <Col span={8}>
          <Card bordered={false}>{renderTreeMenu("COMMON")}</Card>
        </Col>
        <Col span={16}>
          <Card bordered={false}>{renderSelectedMenu("COMMON")}</Card>
        </Col>
      </Row>
    </div>
  );
};

export default MenuManagement;
